import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  AddPhotoAlternate as AddPhotoIcon,
  InsertDriveFile as FileIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  createAiMenuImport,
  getAiMenuImport,
  retryAiMenuImport,
} from 'redux/actions/menuAi';
import ImportMenuLayout, { ACCENT } from './ImportMenuLayout';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];
const ACCEPT_ATTRIBUTE = 'image/png,image/jpeg,application/pdf';
const MAX_FILES = 10;
const POLL_INTERVAL_MS = 3000;
/** Extraction of a large menu can legitimately take a couple of minutes. */
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const STAGES = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  PARSING: 'parsing',
  IMPORTING: 'importing',
  FAILED: 'failed',
};

/**
 * "AI Import" view: upload menu photos, let the menu-ai-parser service turn
 * them into import JSON, then hand that JSON to the caller's existing import
 * action. Shares `ImportMenuLayout` with `ImportMenuView`, which keeps owning
 * the manual JSON-upload flow, so only the import card differs between tabs.
 *
 * @param {string} businessUuid
 * @param {'pos'|'regular'} target                which import format to request
 * @param {(menu: object) => Promise} onImport    throws on failure
 * @param {() => Promise} onEmpty                 throws on failure
 */
const AiImportMenuView = ({
  businessUuid,
  target,
  onImport,
  onEmpty,
  breakGlassPage,
  breakGlassEventType = 'menu-import-action',
  emptyActionSummary = 'Empty menu for this business',
  emptySuccessMessage = 'Menu emptied successfully.',
  title = 'AI Import Menu',
  subtitle = 'Upload photos of a menu and let AI turn them into an imported menu.',
  importSuccessMessage = 'Menu imported successfully.',
  comingSoonProviders,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const pollTimerRef = useRef(null);
  const cancelledRef = useRef(false);
  // Mirrors `previews` so unmount cleanup can revoke the object URLs that exist
  // at that moment rather than the ones captured when the effect was created.
  const previewsRef = useRef([]);

  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [instructions, setInstructions] = useState('');
  const [stage, setStage] = useState(STAGES.IDLE);
  const [jobUuid, setJobUuid] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const busy = stage !== STAGES.IDLE && stage !== STAGES.FAILED;
  const activeStep =
    stage === STAGES.IMPORTING ? 2 : files.length > 0 || busy ? 1 : 0;

  // Object URLs for the thumbnails are revoked on unmount, as is any in-flight
  // poll, so navigating away mid-extraction leaves nothing running.
  useEffect(
    () => () => {
      cancelledRef.current = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      previewsRef.current.forEach((preview) => {
        if (preview.url) URL.revokeObjectURL(preview.url);
      });
    },
    []
  );

  const replaceFiles = (nextFiles) => {
    previewsRef.current.forEach((preview) => {
      if (preview.url) URL.revokeObjectURL(preview.url);
    });
    const nextPreviews = nextFiles.map((file) => ({
      name: file.name,
      isImage: file.type.startsWith('image/'),
      url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));
    previewsRef.current = nextPreviews;
    setFiles(nextFiles);
    setPreviews(nextPreviews);
  };

  const ensureBusiness = () => {
    if (!businessUuid) {
      toast.error(t('No business selected'));
      return false;
    }
    return true;
  };

  const handleChooseFiles = () => {
    if (!ensureBusiness()) return;
    fileInputRef.current?.click();
  };

  const handleFilesChange = (event) => {
    const selected = Array.from(event.target.files || []);
    // Reset so selecting the same files again re-triggers change
    event.target.value = '';
    if (selected.length === 0) return;
    if (!ensureBusiness()) return;

    const rejected = selected.filter(
      (file) => !ACCEPTED_TYPES.includes(file.type)
    );
    if (rejected.length > 0) {
      toast.error(
        t('Unsupported file type. Please upload PNG, JPEG, or PDF files.')
      );
      return;
    }
    if (selected.length > MAX_FILES) {
      toast.error(t(`You can upload up to ${MAX_FILES} files at a time.`));
      return;
    }

    replaceFiles(selected);
    setErrorMessage('');
    setWarnings([]);
    setJobUuid(null);
    setStage(STAGES.IDLE);
  };

  const handleRemoveFile = (index) => {
    const next = files.filter((_, i) => i !== index);
    replaceFiles(next);
  };

  const importMenu = async (menu) => {
    setStage(STAGES.IMPORTING);
    try {
      await onImport(menu);
      if (cancelledRef.current) return;
      replaceFiles([]);
      setInstructions('');
      setJobUuid(null);
      setStage(STAGES.IDLE);
      setSuccessMessage(t(importSuccessMessage));
    } catch {
      if (cancelledRef.current) return;
      // The request interceptor already toasted the reason.
      setStage(STAGES.FAILED);
      setErrorMessage(
        t('The menu was generated but importing it failed. Please try again.')
      );
    }
  };

  const pollUntilDone = (uuid, deadline) => {
    pollTimerRef.current = setTimeout(async () => {
      if (cancelledRef.current) return;

      let job;
      try {
        job = await dispatch(getAiMenuImport(uuid));
      } catch {
        if (cancelledRef.current) return;
        setStage(STAGES.FAILED);
        setErrorMessage(t('Lost contact with the menu parser.'));
        return;
      }
      if (cancelledRef.current) return;

      if (job.status === 'succeeded') {
        setWarnings(job.warnings || []);
        await importMenu(job.menu);
        return;
      }
      if (job.status === 'failed') {
        setStage(STAGES.FAILED);
        setErrorMessage(job.error || t('Menu extraction failed.'));
        return;
      }
      if (Date.now() > deadline) {
        setStage(STAGES.FAILED);
        setErrorMessage(
          t('Menu extraction is taking longer than expected. Try again.')
        );
        return;
      }

      pollUntilDone(uuid, deadline);
    }, POLL_INTERVAL_MS);
  };

  const handleGenerate = async () => {
    if (!ensureBusiness()) return;
    if (files.length === 0) {
      toast.error(t('Please upload at least one menu image first'));
      return;
    }

    setErrorMessage('');
    setWarnings([]);
    setStage(STAGES.UPLOADING);

    let job;
    try {
      job = await dispatch(
        createAiMenuImport({
          businessUuid,
          target,
          instructions: instructions.trim(),
          files,
        })
      );
    } catch {
      setStage(STAGES.FAILED);
      setErrorMessage(t('Could not start the menu extraction.'));
      return;
    }

    setJobUuid(job.uuid);
    setStage(STAGES.PARSING);
    pollUntilDone(job.uuid, Date.now() + POLL_TIMEOUT_MS);
  };

  const handleRetry = async () => {
    if (!jobUuid) {
      handleGenerate();
      return;
    }

    setErrorMessage('');
    setStage(STAGES.PARSING);
    try {
      await dispatch(retryAiMenuImport(jobUuid));
    } catch {
      setStage(STAGES.FAILED);
      setErrorMessage(t('Could not restart the menu extraction.'));
      return;
    }
    pollUntilDone(jobUuid, Date.now() + POLL_TIMEOUT_MS);
  };

  const stageLabel = {
    [STAGES.UPLOADING]: t('Uploading images...'),
    [STAGES.PARSING]: t('Reading the menu with AI. This can take a minute...'),
    [STAGES.IMPORTING]: t('Importing the generated menu...'),
  }[stage];

  return (
    <ImportMenuLayout
      businessUuid={businessUuid}
      breakGlassPage={breakGlassPage}
      breakGlassEventType={breakGlassEventType}
      emptyActionSummary={emptyActionSummary}
      emptySuccessMessage={emptySuccessMessage}
      onEmpty={onEmpty}
      busy={busy}
      successMessage={successMessage}
      onSuccess={setSuccessMessage}
      onSuccessClose={() => setSuccessMessage('')}
      title={title}
      subtitle={subtitle}
      comingSoonProviders={comingSoonProviders}
    >
      {({ disabled }) => (
        <Card
          elevation={0}
          sx={{
            height: '100%',
            border: '1px solid #E5E7EB',
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 138, 0, 0.12)',
                  color: ACCENT,
                }}
              >
                <AutoAwesomeIcon />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('Generate menu from images')}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 2.5 }}>
              {t(
                'Upload photos or a PDF of the menu. The generated menu is imported automatically once it passes validation.'
              )}
            </Typography>

            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                mb: 3,
                '& .MuiStepIcon-root.Mui-active': { color: ACCENT },
                '& .MuiStepIcon-root.Mui-completed': { color: ACCENT },
              }}
            >
              <Step>
                <StepLabel>{t('Upload images')}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('Generate')}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('Import')}</StepLabel>
              </Step>
            </Stepper>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_ATTRIBUTE}
              style={{ display: 'none' }}
              onChange={handleFilesChange}
            />

            {files.length === 0 ? (
              <Box
                onClick={
                  !disabled && businessUuid ? handleChooseFiles : undefined
                }
                sx={{
                  border: '2px dashed #D1D5DB',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: disabled || !businessUuid ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease',
                  opacity: !businessUuid ? 0.6 : 1,
                  '&:hover': {
                    borderColor: disabled || !businessUuid ? '#D1D5DB' : ACCENT,
                    backgroundColor:
                      disabled || !businessUuid
                        ? 'transparent'
                        : 'rgba(255, 138, 0, 0.04)',
                  },
                }}
              >
                <AddPhotoIcon sx={{ color: '#9CA3AF', fontSize: 32 }} />
                <Typography
                  variant="body2"
                  sx={{ color: '#374151', fontWeight: 500, mt: 1 }}
                >
                  {t('Click to choose menu images')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                  {t('Accepted formats: PNG, JPEG, PDF')}
                </Typography>
              </Box>
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                {previews.map((preview, index) => (
                  <Box
                    key={`${preview.name}-${index}`}
                    sx={{
                      position: 'relative',
                      width: 104,
                      height: 104,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid #E5E7EB',
                      backgroundColor: '#F9FAFB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {preview.isImage ? (
                      <Box
                        component="img"
                        src={preview.url}
                        alt={preview.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Stack alignItems="center" spacing={0.5} sx={{ px: 1 }}>
                        <FileIcon sx={{ color: '#9CA3AF' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#6B7280',
                            textAlign: 'center',
                            wordBreak: 'break-all',
                          }}
                        >
                          {preview.name}
                        </Typography>
                      </Stack>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      disabled={disabled}
                      aria-label={t('Remove file')}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': { backgroundColor: '#FFFFFF' },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
              disabled={disabled}
              label={t('Additional instructions (optional)')}
              placeholder={t(
                'For example: prices are in the right column, skip the drinks section, all pizzas come in Small and Large.'
              )}
              sx={{ mt: 2.5 }}
            />

            {busy && (
              <Box sx={{ mt: 2.5 }}>
                <LinearProgress
                  sx={{
                    borderRadius: 1,
                    '& .MuiLinearProgress-bar': { backgroundColor: ACCENT },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: '#6B7280', mt: 1, display: 'block' }}
                >
                  {stageLabel}
                </Typography>
              </Box>
            )}

            {stage === STAGES.FAILED && (
              <Alert severity="error" sx={{ mt: 2.5 }}>
                <AlertTitle>{t('Menu extraction failed')}</AlertTitle>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{errorMessage}</Box>
              </Alert>
            )}

            {warnings.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2.5 }}>
                <AlertTitle>{t('Imported with warnings')}</AlertTitle>
                {warnings.map((warning) => (
                  <Box key={warning}>{warning}</Box>
                ))}
              </Alert>
            )}

            <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
              <Button
                variant="outlined"
                startIcon={<AddPhotoIcon />}
                onClick={handleChooseFiles}
                disabled={disabled || !businessUuid}
                sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
              >
                {files.length > 0 ? t('Replace') : t('Upload images')}
              </Button>
              {stage === STAGES.FAILED ? (
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  startIcon={<RefreshIcon />}
                  onClick={handleRetry}
                  disabled={disabled || !businessUuid || files.length === 0}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: ACCENT,
                    '&:hover': { backgroundColor: '#E67C00' },
                  }}
                >
                  {t('Retry')}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  startIcon={
                    busy ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <AutoAwesomeIcon />
                    )
                  }
                  onClick={handleGenerate}
                  disabled={disabled || !businessUuid || files.length === 0}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: ACCENT,
                    '&:hover': { backgroundColor: '#E67C00' },
                  }}
                >
                  {t('Generate and import')}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </ImportMenuLayout>
  );
};

export default AiImportMenuView;
